// "Revenue" card of a ShareJet booking: the breakdown, per-user payments and unattributed payments.
import { Card, Descriptions, Divider, Space, Table, Tag, Typography } from 'antd';

import type { AdminBookingDetail } from '../../api/types';
import { amount, commissionLabel, gbp } from '../../data/format';
import { PaymentAttemptsTable } from './PaymentAttemptsTable';
import { CANCELLATION_FATE_COLORS, statusLabel } from '../../data/statuses';
import { PaymentTagRow, RefundOutsideAppTag, ReleaseFailedTag } from '../../components/CancellationMoneyTags';

const { Text } = Typography;

export function RevenueCard({ b }: { b: AdminBookingDetail }) {
  return (
    <Card title="Revenue">
      <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
        <Descriptions.Item label="Aircraft MGAV">
          {gbp(b.revenue.aircraftMgavGbp)}
        </Descriptions.Item>
        <Descriptions.Item label="Seats booked">{b.revenue.seatsBooked}</Descriptions.Item>
        <Descriptions.Item label="Operator revenue">
          {gbp(b.revenue.operatorRevenueGbp)}
          {b.revenue.operatorRevenueCurrency && b.revenue.operatorRevenueInCurrency != null
            ? ` (${amount(b.revenue.operatorRevenueInCurrency)} ${b.revenue.operatorRevenueCurrency})`
            : ''}
        </Descriptions.Item>
        <Descriptions.Item label={commissionLabel(b.revenue.processingFeePct)}>
          {gbp(b.revenue.processingCommissionGbp)}
        </Descriptions.Item>
        <Descriptions.Item label="ShareJet margin">
          {gbp(b.revenue.shareJetMarginGbp)}
        </Descriptions.Item>
        <Descriptions.Item label="Total revenue">
          <Text strong>{gbp(b.revenue.totalRevenueGbp)}</Text>
        </Descriptions.Item>
        {/* SHAR-1374 — the fixed fee is not revenue, but the users pay it: shown so the
            figures here add up to the "Total paid" column below. */}
        <Descriptions.Item label="Fixed fee">{gbp(b.revenue.fixedFeeGbp)}</Descriptions.Item>
        <Descriptions.Item label="Total paid by users (incl. fixed fee)">
          <Text strong>{gbp(b.revenue.customerTotalGbp)}</Text>
        </Descriptions.Item>
      </Descriptions>
      {/* SHAR-1091 (PO, 29 Sep) — revenue is ONLY money captured and kept. What
          the seat-holders held when the booking was cancelled is shown apart
          from it, labelled as history, so the two can never be read as one. */}
      {b.revenue.atCancellation ? (
        <>
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Revenue counts only money that was captured and kept. The figures below are what was held when the
            booking was cancelled - history, not revenue.
          </Text>
          <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small" style={{ marginTop: 8 }}>
            <Descriptions.Item label="Seats at cancellation">{b.revenue.atCancellation.seats}</Descriptions.Item>
            <Descriptions.Item label="Held at cancellation (not revenue)">
              {gbp(b.revenue.atCancellation.heldGbp)}
            </Descriptions.Item>
          </Descriptions>
        </>
      ) : null}
      <Divider style={{ margin: '16px 0 8px' }} />
      <Text type="secondary">Per-user payments</Text>
      {/* SHAR-495 — the standalone Payments block was removed; expanding a
          user shows every payment attempt it used to list (kind, status,
          amount, card, failure reason, date), so nothing was lost. */}
      <Table<AdminBookingDetail['userPayments'][number]>
        rowKey="userId"
        style={{ marginTop: 8 }}
        dataSource={b.userPayments}
        pagination={false}
        size="small"
        locale={{ emptyText: 'No users' }}
        expandable={{
          rowExpandable: (u) => (u.attempts?.length ?? 0) > 0,
          expandedRowRender: (u) => <PaymentAttemptsTable rows={u.attempts ?? []} />,
        }}
        columns={[
          {
            title: 'User',
            dataIndex: 'name',
            key: 'name',
            // SHAR-593 — a cancelled holder stays in this table rather than
            // dropping into the unattributed block with no identity.
            //
            // Admin regression 6 Oct — a long UNBROKEN name set the column's
            // minimum width to the whole name, so the table outgrew its card and
            // the entire page scrolled sideways. The name may now break anywhere
            // (overflow-wrap: anywhere also lowers the cell's min-content width),
            // so it wraps inside the column, as the Route cells do (SHAR-734).
            render: (name: string, u) =>
              u.cancelled ? (
                <span style={{ overflowWrap: 'anywhere' }}>
                  <Text delete type="secondary">{name}</Text>{' '}
                  <Tag>Cancelled</Tag>
                </span>
              ) : (
                <span style={{ overflowWrap: 'anywhere' }}>{name}</span>
              ),
          },
          {
            title: 'Price / seat',
            dataIndex: 'pricePerSeatGbp',
            key: 'pps',
            render: (v: number) => gbp(v),
          },
          { title: 'Seats', dataIndex: 'seats', key: 'seats' },
          {
            title: 'Total paid',
            dataIndex: 'totalPaidGbp',
            key: 'total',
            render: (v: number) => gbp(v),
          },
          {
            // SHAR-1091 — what the user held when cancelled, and what became of
            // it. History only: "Seats" and "Total paid" stay the live figures.
            title: 'At cancellation',
            key: 'atCancellation',
            render: (_, u) => {
              const a = u.atCancellation;
              if (!a) return '—';
              // a hold is "Released" only if the provider let it go
              const releaseFailed = !!u.attempts?.some((p) => p.releaseFailedAt && p.status === 'authorized');
              // a refused release that later lapsed: it expired, it was never released
              const expiredAfterRefusal = !!u.attempts?.some((p) => p.releaseFailedAt && p.status === 'expired');
              const C = CANCELLATION_FATE_COLORS;
              const fate = {
                charge: { c: C.captured, l: 'Captured' },
                hold: releaseFailed
                  ? { c: C.stillHeld, l: 'Still held' }
                  : expiredAfterRefusal
                    ? { c: C.expired, l: 'Expired' }
                    : { c: C.released, l: 'Released' },
                lapsed: { c: C.expired, l: 'Expired' },
                none: { c: C.nothingHeld, l: 'Nothing held' },
                refunded: { c: C.refunded, l: 'Refunded' },
              } as const;
              return (
                <Space size={6} wrap>
                  <span>
                    {a.seats} {a.seats === 1 ? 'seat' : 'seats'} · {gbp(a.heldGbp)}
                  </span>
                  <Tag color={fate[a.kind].c}>{fate[a.kind].l}</Tag>
                </Space>
              );
            },
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (s: 'paid' | 'pre_auth' | 'not_paid' | 'refunded' | 'released' | 'payment_required', u) => {
              const map = {
                paid: { c: 'green', l: 'Paid' },
                pre_auth: { c: 'blue', l: 'Pre-auth' },
                not_paid: { c: 'red', l: 'Not paid' },
                // SHAR-159 — the pre-auth lapsed and the user has not paid again.
                payment_required: { c: 'red', l: 'Payment required' },
                refunded: { c: 'orange', l: 'Refunded' }, // SHAR-107
                // SHAR-593 — the hold was let go when the user was cancelled.
                released: { c: 'default', l: 'Released' },
              } as const;
              // what a cancellation left on this user's payments, without expanding the row
              const refundAt = u.attempts?.find((a) => a.refundOutsideAppAt)?.refundOutsideAppAt;
              // only a hold that still stands: one that expired since is gone
              const failed = u.attempts?.find((a) => a.releaseFailedAt && a.status === 'authorized');
              return (
                <PaymentTagRow>
                  <Tag color={map[s]?.c ?? 'default'}>{statusLabel({ [s]: map[s]?.l }, s, u.statusLabel)}</Tag>
                  <RefundOutsideAppTag at={refundAt} />
                  <ReleaseFailedTag at={failed?.releaseFailedAt} reason={failed?.releaseFailure} />
                </PaymentTagRow>
              );
            },
          },
        ]}
      />
      {/* SHAR-495 — payments whose payer is no longer a seat-holder (cancelled
          and refunded users) belong to no row above; keep them visible. */}
      {b.unattributedPayments?.length ? (
        <>
          <Divider style={{ margin: '16px 0 8px' }} />
          <Text type="secondary">Payments with no current seat-holder</Text>
          <div style={{ marginTop: 8 }}>
            <PaymentAttemptsTable rows={b.unattributedPayments} />
          </div>
        </>
      ) : null}
    </Card>
  );
}
